/**
 * ClientLoggerProvider
 *
 * - Initializes debug mode detection (?debug + localStorage) on mount.
 * - Provides the useClientLogger context to descendants.
 * - Renders children + (optionally) a debug panel can live outside or inside.
 *
 * Place near the top of your tree, inside AuthProvider so that "Send to Support"
 * can rely on the user's session cookie.
 */

import * as React from "react";

import {
  clearBuffer,
  flush as rawFlush,
  isEnabled,
  sendToSupport as rawSendToSupport,
  setEnabled as rawSetEnabled,
  initFromEnvironment,
} from "@/lib/client-logger";
import type { ClientLoggerContextValue } from "@/hooks/useClientLogger";

const ClientLoggerContext = React.createContext<ClientLoggerContextValue | null>(null);

// Stable action functions for context (live `enabled`/`logs` should be read via the useClientLogger() hook directly)
const stableSetEnabled = rawSetEnabled;
const stableClear = clearBuffer;
const stableFlush = rawFlush;
const stableSendToSupport = rawSendToSupport;

export function ClientLoggerProvider({ children }: { children: React.ReactNode }) {
  // Run once on mount — detects ?debug=true and persisted flag
  React.useEffect(() => {
    initFromEnvironment();
  }, []);

  // Provide a stable context value. Note: `enabled` and `logs` here are snapshots at mount time.
  // For live updating values, call `useClientLogger()` directly in the component that needs them
  // (this avoids re-rendering the entire app tree on every debug log entry).
  const value = React.useMemo<ClientLoggerContextValue>(() => ({
    enabled: isEnabled(),
    logs: [],
    setEnabled: stableSetEnabled,
    clear: stableClear,
    flush: stableFlush,
    sendToSupport: stableSendToSupport,
  }), []);

  return (
    <ClientLoggerContext.Provider value={value}>
      {children}
    </ClientLoggerContext.Provider>
  );
}

export function useClientLoggerContext(): ClientLoggerContextValue {
  const ctx = React.useContext(ClientLoggerContext);
  if (!ctx) {
    throw new Error("useClientLoggerContext must be used inside <ClientLoggerProvider>");
  }
  return ctx;
}

// Re-export the hook for convenience (most components just use the hook)
export { useClientLogger } from "@/hooks/useClientLogger";
